/**
 * Media Library Page
 * Personal media management interface with Carbon Tabs
 * Epic 75 - Work Item 3: R2 Object Storage and Content Delivery Architecture
 */

import React, { useState } from 'react';
import {
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  Button,
  Grid,
  Column,
  Breadcrumb,
  BreadcrumbItem,
  InlineNotification,
} from '@carbon/react';
import { CloudUpload, FolderAdd, Settings, Analytics } from '@carbon/react/icons';
import { FileUploader } from '../components/storage/FileUploader';
import { MediaGallery } from '../components/storage/MediaGallery';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

const MediaLibrary: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const handleUploadComplete = (fileData: any) => {
    setUploadSuccess(true);
    setTimeout(() => {
      setUploadSuccess(false);
      setActiveTab(0); // Switch to gallery view
    }, 3000);
  };

  const handleFileDelete = async (fileId: string) => {
    try {
      const response = await fetch(`/api/v1/storage/files/${fileId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete file');
      }

      // Refresh gallery
      window.location.reload();
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const handleFileShare = async (fileId: string) => {
    try {
      const response = await fetch(`/api/v1/storage/share/${fileId}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          expiresIn: 7 * 24 * 60 * 60, // 7 days
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate share link');
      }

      const data = await response.json();
      // Copy to clipboard
      navigator.clipboard.writeText(data.shareUrl);
      alert('Share link copied to clipboard!');
    } catch (error) {
      console.error('Share error:', error);
    }
  };

  return (
    <div className="media-library-page">
      <Grid fullWidth>
        <Column lg={16} md={8} sm={4}>
          {/* Breadcrumb */}
          <Breadcrumb className="mb-4">
            <BreadcrumbItem href="/">Home</BreadcrumbItem>
            <BreadcrumbItem href="/dashboard">Dashboard</BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>Media Library</BreadcrumbItem>
          </Breadcrumb>

          {/* Page Header */}
          <div className="page-header mb-6">
            <h1 className="text-3xl font-semibold mb-2">My Media Library</h1>
            <p className="text-gray-600">Manage your files, documents, and media content in one place</p>
          </div>

          {/* Success Notification */}
          {uploadSuccess && (
            <InlineNotification
              kind="success"
              title="Upload Complete"
              subtitle="Your file has been successfully uploaded to your media library"
              className="mb-4"
            />
          )}

          {/* Main Content Tabs */}
          <Tabs selectedIndex={activeTab} onChange={(e: any) => setActiveTab(e.selectedIndex)}>
            <TabList aria-label="Media Library Navigation">
              <Tab>My Files</Tab>
              <Tab>Upload</Tab>
              <Tab>Shared with Me</Tab>
              <Tab>Storage Analytics</Tab>
            </TabList>
            <TabPanels>
              {/* My Files Tab */}
              <TabPanel>
                <div className="tab-content">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-medium">Your Files</h2>
                    <div className="flex gap-2">
                      <Button size="sm" kind="secondary" renderIcon={FolderAdd} onClick={() => {}}>
                        New Folder
                      </Button>
                      <Button size="sm" kind="primary" renderIcon={CloudUpload} onClick={() => setActiveTab(1)}>
                        Upload Files
                      </Button>
                    </div>
                  </div>
                  <MediaGallery
                    userId={user?.id}
                    onFileDelete={handleFileDelete}
                    onFileShare={handleFileShare}
                    enableBulkOperations
                  />
                </div>
              </TabPanel>

              {/* Upload Tab */}
              <TabPanel>
                <div className="tab-content">
                  <h2 className="text-xl font-medium mb-4">Upload Files</h2>
                  <FileUploader onUploadComplete={handleUploadComplete} onError={error => console.error(error)} />
                </div>
              </TabPanel>

              {/* Shared with Me Tab */}
              <TabPanel>
                <div className="tab-content">
                  <h2 className="text-xl font-medium mb-4">Shared Files</h2>
                  <p className="text-gray-600 mb-4">Files that have been shared with you by other users</p>
                  <MediaGallery userId={user?.id} category="shared" enableBulkOperations={false} />
                </div>
              </TabPanel>

              {/* Storage Analytics Tab */}
              <TabPanel>
                <div className="tab-content">
                  <h2 className="text-xl font-medium mb-4">Storage Analytics</h2>
                  <Grid>
                    <Column lg={8} md={4} sm={4}>
                      <div className="bg-white p-4 rounded shadow">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-gray-600">Total Storage Used</span>
                          <Analytics size={20} />
                        </div>
                        <div className="text-2xl font-bold">2.3 GB</div>
                        <div className="text-sm text-gray-500">of 10 GB</div>
                        <div className="mt-2 bg-gray-200 rounded-full h-2">
                          <div className="bg-blue-500 h-2 rounded-full" style={{ width: '23%' }} />
                        </div>
                      </div>
                    </Column>
                    <Column lg={4} md={2} sm={2}>
                      <div className="bg-white p-4 rounded shadow">
                        <div className="text-gray-600 mb-2">Total Files</div>
                        <div className="text-2xl font-bold">127</div>
                      </div>
                    </Column>
                    <Column lg={4} md={2} sm={2}>
                      <div className="bg-white p-4 rounded shadow">
                        <div className="text-gray-600 mb-2">Shared Files</div>
                        <div className="text-2xl font-bold">23</div>
                      </div>
                    </Column>
                  </Grid>
                  <div className="mt-6">
                    <h3 className="text-lg font-medium mb-3">Storage by Type</h3>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-3 bg-white rounded">
                        <span>Documents</span>
                        <span className="font-medium">450 MB</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white rounded">
                        <span>Images</span>
                        <span className="font-medium">1.2 GB</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white rounded">
                        <span>Videos</span>
                        <span className="font-medium">650 MB</span>
                      </div>
                    </div>
                  </div>
                </div>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Column>
      </Grid>
    </div>
  );
};

export default MediaLibrary;
